import type { HorizontalUnit } from "@lumidb/lumidb";
import * as THREE from "three";
import { proxy } from "valtio";
import type { Annotations } from "./annotations";
import { App } from "./app";
import { getCachedElevation } from "./dtm";
import {
    type ClickEvent,
    type DragEvent,
    type DragStartEvent,
    type InputEvent,
    type KeyEvent,
    type ZoomEvent,
} from "./input-events";
import { DOUBLE_CLICK_TIME } from "./input-manager";
import { LOD } from "./lod";
import { Project } from "./project";
import { Terrain } from "./terrain";
import { metersToScene, UNIT_X, UNIT_Z, WEB_MERCATOR_MAX, WM_EPSG } from "./utils";
import { FOV_RAD, PointHit, type Viewer } from "./viewer";

const SENSITIVITY_X = 5.0;
const SENSITIVITY_Y = 2.5;
const PIVOT_SCALE = 24;
const TOUCH_PITCH_THRESHOLD = 0.8;
const TOUCH_ROLL_THRESHOLD = 0.02;
const MIN_DISTANCE_M = 0.1;

// Only switch to a projection when it covers at least half of the focus area
const MIN_PROJ_COVERAGE = 0.5;

const MAX_CAMERA_DIST_M = 7_500_000;

export const maxCameraDist = (sceneUnit: HorizontalUnit | null) => metersToScene(MAX_CAMERA_DIST_M, sceneUnit);

const MAX_ORTHO_WIDTH_M = 5 * MAX_CAMERA_DIST_M;

type ControlState = "pan" | "rotate_pivot" | "rotate_cam" | "gesture" | "idle";

const ZERO_PLANE = new THREE.Plane(UNIT_Z, 0);

export class EarthControls {
    public state = proxy({
        changedAt: performance.now(),
        controlState: "idle" as ControlState,
        orbiting: false,
        lastGesture: null as null | "pan" | "rotate" | "zoom",
    });

    private camStart: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    private hidePivotHandle = 0;

    private temp = {
        dragStart: new THREE.Vector2(),
        camFwd: new THREE.Vector3(),
        camRight: new THREE.Vector3(),
        camUp: new THREE.Vector3(),
        pivotToCam: new THREE.Vector3(),
        panPlane: new THREE.Plane(),
        panRay: new THREE.Ray(),
        intersection: new THREE.Vector3(),
        targetToCam: new THREE.Vector3(),
        panAmount: new THREE.Vector3(),
        prev: {
            angle: new THREE.Vector2(),
            touchesDirection: 0,
            touchAngle: 0,
            touchDistance: 0,
        },
    };

    private wasd = {
        right: false,
        left: false,
        forward: false,
        back: false,
        up: false,
        down: false,
        speedBoost: false,
        baseMovementSpeedM: 50.0,
        movement: new THREE.Vector3(),
    };

    private readonly viewer: Viewer;
    private readonly app: App;
    private readonly lod: LOD;
    private readonly terrain: Terrain;
    private readonly project: Project;
    private readonly annotations?: Annotations;

    constructor(app: App, viewer: Viewer, lod: LOD, terrain: Terrain, project: Project, annotations?: Annotations) {
        this.terrain = terrain;
        this.app = app;
        this.viewer = viewer;
        this.lod = lod;
        this.project = project;
        this.annotations = annotations;

        this.camStart = this.viewer.camera.clone();
    }

    private intersects(screenPos: THREE.Vector2): PointHit | null {
        const point = this.lod.pickPoint(screenPos);
        if (point) {
            return point;
        }

        const mapHit = this.terrain.getHeightAtScreenPosition(screenPos, this.temp.intersection);
        if (mapHit) {
            return {
                type: "point",
                position: mapHit,
                tile: null,
            };
        }

        // Fallback: check mouse intersection with a plane at Z=0
        const ray = this.viewer.getMouseRay(screenPos, this.viewer.camera);
        const zeroPlaneIntersection = ray.intersectPlane(ZERO_PLANE, this.temp.intersection);

        if (zeroPlaneIntersection) {
            return {
                type: "point",
                position: zeroPlaneIntersection,
                tile: null,
            };
        }

        return null;
    }

    public handleInput(event: InputEvent): boolean {
        let handled = false;

        switch (this.state.controlState) {
            case "idle":
                handled = this.handleIdleInput(event);
                break;
            case "pan":
                handled = this.handlePanInput(event);
                break;
            case "rotate_pivot":
                handled = this.handleRotatePivotInput(event);
                break;
            case "rotate_cam":
                handled = this.handleRotateCamInput(event);
                break;
            case "gesture":
                handled = this.handleGestureInput(event);
                break;
        }

        if (handled) {
            this.state.changedAt = performance.now();

            const state = this.state.controlState;
            if (state === "pan") {
                this.state.lastGesture = "pan";
            } else if (state === "rotate_pivot" || state === "rotate_cam") {
                this.state.lastGesture = "rotate";
            } else if (event.type === "zoom" || event.type === "pinch" || state === "gesture") {
                this.state.lastGesture = "zoom";
            }
        }

        return handled;
    }

    private eval() {
        this.viewer.camera.updateMatrixWorld();
        this.lod.eval();
        this.terrain.eval();
        this.annotations?.eval();
    }

    private getHalfExtent(camera: THREE.OrthographicCamera | THREE.PerspectiveCamera) {
        const offset = this.app.state.offset;

        let halfWidth: number;
        let halfHeight: number;
        if (camera instanceof THREE.OrthographicCamera) {
            halfWidth = (camera.right - camera.left) / 2;
            halfHeight = (camera.top - camera.bottom) / 2;
        } else {
            const height = Math.abs(camera.position.z - offset.z);
            halfHeight = height * Math.tan(FOV_RAD / 2);
            halfWidth = halfHeight * camera.aspect;
        }

        return { halfWidth, halfHeight };
    }

    private clampToMap() {
        if (!this.app.isWm()) return;

        const camera = this.viewer.camera;
        const offset = this.app.state.offset;

        const { halfWidth, halfHeight } = this.getHalfExtent(camera);

        const worldX = camera.position.x + offset.x;
        const worldY = camera.position.y + offset.y;

        const minX = -WEB_MERCATOR_MAX + halfWidth;
        const maxX = WEB_MERCATOR_MAX - halfWidth;
        const minY = -WEB_MERCATOR_MAX + halfHeight;
        const maxY = WEB_MERCATOR_MAX - halfHeight;

        const clampedX = THREE.MathUtils.clamp(worldX, minX, maxX);
        const clampedY = THREE.MathUtils.clamp(worldY, minY, maxY);

        camera.position.x += clampedX - worldX;
        camera.position.y += clampedY - worldY;
    }

    public rotateTo(toPitch: number, toYaw: number) {
        const camera = this.viewer.camera;
        const startQuaternion = camera.quaternion.clone();

        this.temp.pivotToCam.subVectors(camera.position, this.viewer.pivot.position);
        const distance = this.temp.pivotToCam.length();

        const pitchRotation = new THREE.Quaternion().setFromAxisAngle(UNIT_X, toPitch);
        const yawRotation = new THREE.Quaternion().setFromAxisAngle(UNIT_Z, toYaw);
        const targetQuaternion = yawRotation.multiply(pitchRotation);

        // Short-path correction
        if (startQuaternion.dot(targetQuaternion) < 0) {
            targetQuaternion.w *= -1;
            targetQuaternion.x *= -1;
            targetQuaternion.y *= -1;
            targetQuaternion.z *= -1;
        }

        const startTime = performance.now();

        const progressAnimation = () => {
            const now = performance.now();
            const progress = Math.min((now - startTime) / DOUBLE_CLICK_TIME, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            camera.quaternion.slerpQuaternions(startQuaternion, targetQuaternion, eased);
            const offset = new THREE.Vector3(0, 0, distance).applyQuaternion(camera.quaternion);

            camera.position.copy(this.viewer.pivot.position).add(offset);
            this.viewer.updateNow();

            if (progress < 1) {
                requestAnimationFrame(progressAnimation);
            }
        };

        requestAnimationFrame(progressAnimation);
    }

    public wasdActive() {
        return (
            this.wasd.forward || this.wasd.back || this.wasd.left || this.wasd.right || this.wasd.up || this.wasd.down
        );
    }

    private getButton(event: MouseEvent) {
        return event.button === 0 ? "left" : event.button === 1 ? "middle" : event.button === 2 ? "right" : "left";
    }

    public startOrbit() {
        if (this.state.orbiting) {
            return;
        }

        let prev = performance.now();
        this.state.orbiting = true;
        this.temp.pivotToCam.subVectors(this.viewer.camera.position, this.viewer.pivot.position);

        const progressAnimation = () => {
            const now = performance.now();
            const delta = Math.min(now - prev, 1000 / 60);
            prev = now;

            this.temp.pivotToCam.applyAxisAngle(UNIT_Z, delta * 0.0001);
            this.viewer.camera.position.copy(this.viewer.pivot.position).add(this.temp.pivotToCam);
            this.viewer.camera.lookAt(this.viewer.pivot.position);
            this.viewer.camera.updateProjectionMatrix();

            this.viewer.updateNow();

            if (this.state.orbiting) {
                requestAnimationFrame(progressAnimation);
            }
        };

        requestAnimationFrame(progressAnimation);
    }

    public stopOrbit() {
        this.state.orbiting = false;
    }

    private activateWASD(direction: "forward" | "back" | "left" | "right" | "up" | "down") {
        const wasActive = this.wasdActive();
        this.wasd[direction] = true;
        let prev = performance.now();

        if (wasActive) {
            return;
        }

        const move = () => {
            if (!this.wasdActive()) {
                return;
            }

            const now = performance.now();
            const delta = Math.min(now - prev, 100) / 1000;
            prev = now;

            const camera = this.viewer.camera;

            if (!(camera instanceof THREE.PerspectiveCamera)) {
                return;
            }

            const movement = this.wasd.movement.set(0, 0, 0);
            const forward = camera.getWorldDirection(this.temp.camFwd);
            const right = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);

            if (this.wasd.forward) movement.add(forward);
            if (this.wasd.back) movement.sub(forward);
            if (this.wasd.right) movement.add(right);
            if (this.wasd.left) movement.sub(right);
            if (this.wasd.up) movement.z += 1;
            if (this.wasd.down) movement.z -= 1;

            if (movement.lengthSq() > 0) {
                movement.normalize();

                const sceneSpeed = metersToScene(this.wasd.baseMovementSpeedM, this.app.state.sceneUnit);
                const speed = sceneSpeed * (this.wasd.speedBoost ? 3 : 1);

                movement.multiplyScalar(speed * delta);
                camera.position.add(movement);

                this.state.changedAt = performance.now();
                this.viewer.updateNow();
                this.eval();
            }

            requestAnimationFrame(move);
        };

        requestAnimationFrame(move);
    }

    private getPivotScalar() {
        const camera = this.viewer.camera;

        if (camera instanceof THREE.OrthographicCamera) {
            const unitsPerPixel = (camera.right - camera.left) / this.viewer.size.width;
            return unitsPerPixel * 12;
        }

        return (
            (this.viewer.pivot.position.distanceTo(camera.position) * PIVOT_SCALE) /
            Math.max(this.viewer.size.width, this.viewer.size.height)
        );
    }

    private handleIdleInput(event: InputEvent): boolean {
        switch (event.type) {
            case "pointer-down":
                return this.handlePointerDown(event);
            case "pointer-up":
                return this.handlePointerUp(event);
            case "drag-start":
                return this.handleDragStart(event);
            case "double-click":
                return this.handleDoubleClick(event);
            case "zoom":
                return this.handleZoom(event);
            case "key-down":
                return this.handleKeyDown(event);
            case "key-up":
                return this.handleKeyUp(event);
        }
        return false;
    }

    private handlePanInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag":
                if (event.touches?.length === 2) {
                    return this.transitionToGesture(event);
                } else {
                    return this.handlePanDrag(event);
                }
            case "drag-end":
                return this.handleDragEnd();
            case "pointer-up":
                return this.handlePointerUp(event);
            case "key-down":
                return this.handleKeyDown(event);
            case "key-up":
                return this.handleKeyUp(event);
            default:
                return false;
        }
    }

    private handleRotatePivotInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag": {
                if (event.touches?.length === 2) {
                    return this.transitionToGesture(event);
                } else {
                    return this.handleRotatePivotDrag(event);
                }
            }
            case "drag-end":
                return this.handleDragEnd();
            case "pointer-up":
                return this.handlePointerUp(event);
            case "key-down":
                return this.handleKeyDown(event);
            case "key-up":
                return this.handleKeyUp(event);
            default:
                return false;
        }
    }

    private handleRotateCamInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag": {
                if (event.touches?.length === 2) {
                    return this.transitionToGesture(event);
                } else {
                    return this.handleRotateCamDrag(event);
                }
            }
            case "drag-end":
                return this.handleDragEnd();
            case "key-down":
                return this.handleKeyDown(event);
            case "key-up":
                return this.handleKeyUp(event);
            default:
                return false;
        }
    }

    private handleGestureInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag":
                return this.handleGestureDrag(event);
            case "drag-end":
                return this.handleDragEnd();
            case "pointer-up":
                return this.handlePointerUp(event);
            default:
                return false;
        }
    }

    private handlePointerDown(event: ClickEvent): boolean {
        this.stopOrbit();
        const button = this.getButton(event.originalEvent);

        if (button === "middle") {
            return false;
        }

        const point = this.intersects(event.position);

        if (point) {
            this.viewer.pivot.position.copy(point.position);
            this.viewer.pivot.scale.setScalar(this.getPivotScalar());
            this.temp.panPlane.setFromNormalAndCoplanarPoint(UNIT_Z, this.viewer.pivot.position);
            this.viewer.pivot.visible = true;
            this.viewer.update();
        }

        return true;
    }

    private handlePointerUp(_: ClickEvent): boolean {
        if (this.hidePivotHandle !== 0) {
            clearTimeout(this.hidePivotHandle);
        }

        this.hidePivotHandle = window.setTimeout(() => {
            this.viewer.pivot.visible = false;
            this.hidePivotHandle = 0;
            this.viewer.update();
        }, DOUBLE_CLICK_TIME);

        return true;
    }

    private transitionToGesture(event: DragEvent): boolean {
        this.state.controlState = "gesture";

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const touchDistance = touch1.position.distanceTo(touch2.position);
        const touchAngle = Math.atan2(touch2.position.y - touch1.position.y, touch2.position.x - touch1.position.x);

        this.temp.prev.touchDistance = touchDistance;
        this.temp.prev.touchAngle = touchAngle;

        const point = this.intersects(event.position);

        if (point) {
            this.viewer.pivot.position.copy(point.position);
            this.viewer.pivot.scale.setScalar(this.getPivotScalar());
        }

        return this.handleGestureDrag(event);
    }

    private handleDragStart(event: DragStartEvent): boolean {
        const button = this.getButton(event.originalEvent);

        if (button === "right" || (event.originalEvent.shiftKey && button === "left")) {
            this.state.controlState = "rotate_pivot";
        } else if (button === "middle") {
            this.state.controlState = "rotate_cam";
        } else {
            this.state.controlState = "pan";
        }

        const shouldPivot = this.state.controlState !== "rotate_cam";

        if (shouldPivot && !this.viewer.pivot.visible && this.viewer.renderer) {
            const point = this.intersects(event.position);

            if (point) {
                this.viewer.pivot.position.copy(point.position);
                this.viewer.pivot.scale.setScalar(this.getPivotScalar());
                this.temp.panPlane.setFromNormalAndCoplanarPoint(UNIT_Z, this.viewer.pivot.position);
                this.viewer.pivot.visible = true;
            }
        }

        clearTimeout(this.hidePivotHandle);
        this.temp.prev.angle.set(0, 0);
        this.temp.dragStart.copy(event.position);
        this.camStart = this.viewer.camera.clone();

        return true;
    }

    private getRotationDeltas(event: DragEvent) {
        const camera = this.viewer.camera;
        const movement = event.position.clone().sub(this.temp.dragStart);

        const ax = movement.x * SENSITIVITY_X;
        const ay = movement.y * SENSITIVITY_Y;

        const dx = this.temp.prev.angle.x - ax;
        let dy = this.temp.prev.angle.y - ay;

        const cameraDir = camera.getWorldDirection(this.temp.camFwd);
        const pitch = cameraDir.angleTo(UNIT_Z);

        if (pitch + dy > Math.PI) {
            dy = Math.PI - pitch;
        }

        if (pitch + dy < 0) {
            dy = 0 - pitch;
        }

        return { ax, ay, dx, dy };
    }

    private handleRotateCamDrag(event: DragEvent): boolean {
        const camera = this.viewer.camera;
        const { dx, dy, ax, ay } = this.getRotationDeltas(event);
        const camRight = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);

        camera.rotateOnWorldAxis(camRight, -dy);
        camera.rotateOnWorldAxis(UNIT_Z, dx);
        this.temp.prev.angle.set(ax, ay);

        this.viewer.update();
        this.eval();

        return true;
    }

    private handleRotatePivotDrag(event: DragEvent): boolean {
        const camera = this.viewer.camera;
        const { dx, dy, ax, ay } = this.getRotationDeltas(event);

        const camRight = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);

        const pivotToCam = this.temp.pivotToCam.subVectors(camera.position, this.viewer.pivot.position);

        pivotToCam.applyAxisAngle(camRight, -dy);
        pivotToCam.applyAxisAngle(UNIT_Z, dx);
        camera.position.addVectors(this.viewer.pivot.position, pivotToCam);
        camera.rotateOnWorldAxis(camRight, -dy);
        camera.rotateOnWorldAxis(UNIT_Z, dx);
        this.temp.prev.angle.set(ax, ay);

        this.viewer.update();
        this.eval();

        return true;
    }

    private handlePanDrag(event: DragEvent): boolean {
        const camera = this.viewer.camera;
        this.viewer.pivot.visible = true;

        const ray = this.viewer.getMouseRay(event.position, this.camStart);

        const intersection = ray.intersectPlane(this.temp.panPlane, this.temp.intersection);

        if (intersection) {
            this.temp.panAmount.subVectors(this.viewer.pivot.position, intersection);
            camera.position.copy(this.camStart.position).add(this.temp.panAmount);
        }

        this.clampToMap();
        this.viewer.update();
        this.eval();

        return true;
    }

    private handleGestureDrag(event: DragEvent): boolean {
        const camera = this.viewer.camera;

        if (!event.touches || event.touches.length !== 2) {
            return false;
        }

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const touchDistance = touch1.position.distanceTo(touch2.position);
        const currentAngle = Math.atan2(touch2.position.y - touch1.position.y, touch2.position.x - touch1.position.x);
        const angleChange = currentAngle - this.temp.prev.touchAngle;
        const touch1Delta = touch1.position.clone().sub(touch1.previousPosition);
        const touch2Delta = touch2.position.clone().sub(touch2.previousPosition);
        const delta1Normalized = touch1Delta.clone().normalize();
        const delta2Normalized = touch2Delta.clone().normalize();
        const touchesDirection = delta1Normalized.dot(delta2Normalized);
        const touchDistanceDelta = touchDistance - this.temp.prev.touchDistance;

        if (touchesDirection > TOUCH_PITCH_THRESHOLD) {
            if (this.temp.prev.touchesDirection < 0.8) {
                this.temp.dragStart.copy(event.position);
                this.temp.prev.angle.set(0, 0);
            }

            this.handleRotatePivotDrag(event);
        }
        this.temp.prev.touchesDirection = touchesDirection;

        if (Math.abs(angleChange) > TOUCH_ROLL_THRESHOLD) {
            const pivotToCam = this.temp.pivotToCam.subVectors(camera.position, this.viewer.pivot.position);

            pivotToCam.applyAxisAngle(UNIT_Z, -angleChange);
            camera.position.addVectors(this.viewer.pivot.position, pivotToCam);
            camera.rotateOnWorldAxis(UNIT_Z, -angleChange);
            this.temp.prev.touchAngle = currentAngle;
        }

        this.zoomToward(this.viewer.pivot.position, 1 - touchDistanceDelta / this.temp.prev.touchDistance);
        this.temp.prev.touchDistance = touchDistance;

        this.clampToMap();
        this.viewer.update();
        this.eval();

        return true;
    }

    private handleDragEnd(): boolean {
        if (this.hidePivotHandle !== 0) {
            clearTimeout(this.hidePivotHandle);
            this.hidePivotHandle = 0;
        }

        this.viewer.pivot.visible = false;
        this.state.controlState = "idle";
        this.viewer.update();

        return true;
    }

    private handleDoubleClick(event: ClickEvent): boolean {
        const button = this.getButton(event.originalEvent);

        if (button !== "left") {
            return false;
        }

        const point = this.intersects(event.position);

        if (point) {
            this.animateToward(point.position, 0.5);
        }

        return true;
    }

    private handleZoom(event: ZoomEvent): boolean {
        if (!this.viewer.pivot.visible) {
            const point = this.intersects(event.position);

            if (point) {
                this.viewer.pivot.position.copy(point.position);
                this.viewer.pivot.visible = true;
            }
        }

        this.zoomToward(this.viewer.pivot.position, 1.0 - event.delta);

        if (this.hidePivotHandle !== 0) {
            clearTimeout(this.hidePivotHandle);
        }

        this.hidePivotHandle = window.setTimeout(() => {
            this.viewer.pivot.visible = false;
            this.hidePivotHandle = 0;
            this.viewer.update();
        }, DOUBLE_CLICK_TIME);

        return true;
    }

    private handleKeyDown(event: KeyEvent): boolean {
        const camera = this.viewer.camera;
        const code = event.code;

        if (event.originalEvent.metaKey) {
            return false;
        }

        if (camera instanceof THREE.PerspectiveCamera) {
            if (code === "KeyW") {
                this.activateWASD("forward");
                return true;
            }
            if (code === "KeyS") {
                this.activateWASD("back");
                return true;
            }
            if (code === "KeyA") {
                this.activateWASD("left");
                return true;
            }
            if (code === "KeyD") {
                this.activateWASD("right");
                return true;
            }
            if (code === "KeyR") {
                this.activateWASD("up");
                return true;
            }
            if (code === "KeyF") {
                this.activateWASD("down");
                return true;
            }
            if (code === "ShiftLeft" || code === "ShiftRight") {
                this.wasd.speedBoost = true;
                return true;
            }
        }

        if (code === "Space") {
            if (this.state.orbiting) {
                this.stopOrbit();
            } else {
                this.startOrbit();
            }

            return true;
        }

        return false;
    }

    private handleKeyUp(event: KeyEvent): boolean {
        const code = event.code;

        if (code === "KeyW") {
            this.wasd.forward = false;
            return true;
        }
        if (code === "KeyS") {
            this.wasd.back = false;
            return true;
        }
        if (code === "KeyA") {
            this.wasd.left = false;
            return true;
        }
        if (code === "KeyD") {
            this.wasd.right = false;
            return true;
        }
        if (code === "KeyR") {
            this.wasd.up = false;
            return true;
        }
        if (code === "KeyF") {
            this.wasd.down = false;
            return true;
        }
        if (code === "ShiftLeft" || code === "ShiftRight") {
            this.wasd.speedBoost = false;
            return true;
        }

        return false;
    }

    private zoomToward(target: THREE.Vector3, zoomFactor: number) {
        this.viewer.pivot.position.copy(target);
        this.applyZoom(target, zoomFactor);
        this.clampToMap();
        this.viewer.update();
        this.eval();
    }

    private applyZoom(target: THREE.Vector3, zoomFactor: number) {
        const camera = this.viewer.camera;

        let zoomPosition = camera instanceof THREE.PerspectiveCamera;
        let zoomFrustum = camera instanceof THREE.OrthographicCamera;

        if (zoomFactor > 1) {
            if (camera instanceof THREE.PerspectiveCamera) {
                const cameraZ = this.viewer.camera.position.z;
                const offsetZ = this.app.state.offset.z;
                const altitude = Math.abs(cameraZ - offsetZ);

                if (altitude >= maxCameraDist(this.app.state.sceneUnit)) {
                    zoomPosition = false;
                }
            }

            if (camera instanceof THREE.OrthographicCamera) {
                const width = camera.right - camera.left;
                if (width >= metersToScene(MAX_ORTHO_WIDTH_M, this.app.state.sceneUnit)) {
                    zoomFrustum = false;
                }
            }
        }

        if (zoomFrustum && camera instanceof THREE.OrthographicCamera) {
            const pivotBefore = this.viewer.pivot.position.clone().project(camera);
            camera.left *= zoomFactor;
            camera.right *= zoomFactor;
            camera.top *= zoomFactor;
            camera.bottom *= zoomFactor;

            camera.updateProjectionMatrix();

            const pivotAfter = this.viewer.pivot.position.clone().project(camera);
            const dx = pivotAfter.x - pivotBefore.x;
            const dy = pivotAfter.y - pivotBefore.y;
            const { halfWidth, halfHeight } = this.getHalfExtent(camera);

            const fwd = camera.getWorldDirection(this.temp.camFwd);
            const right = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
            const up = this.temp.camUp.crossVectors(right, fwd).normalize();

            camera.position.addScaledVector(right, dx * halfWidth);
            camera.position.addScaledVector(up, dy * halfHeight);
        }

        if (zoomPosition) {
            const minDistance = metersToScene(MIN_DISTANCE_M, this.app.state.sceneUnit);
            const targetToCam = this.temp.targetToCam.subVectors(camera.position, target);
            const movement = targetToCam.multiplyScalar(zoomFactor).clampLength(minDistance, Infinity);

            camera.position.copy(target).add(movement);
        }

        if (zoomPosition || zoomFrustum) {
            this.viewer.pivot.scale.setScalar(this.getPivotScalar());
        }
    }

    restorePivotPosition(oldNDC: THREE.Vector3) {
        const camera = this.viewer.camera;
        const newNDC = this.viewer.pivot.position.clone().project(camera);
        const dx = newNDC.x - oldNDC.x;
        const dy = newNDC.y - oldNDC.y;

        if (camera instanceof THREE.OrthographicCamera) {
            const { halfWidth, halfHeight } = this.getHalfExtent(camera);
            const fwd = camera.getWorldDirection(this.temp.camFwd);
            const right = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
            const up = this.temp.camUp.crossVectors(right, fwd).normalize();
            camera.position.addScaledVector(right, dx * halfWidth);
            camera.position.addScaledVector(up, dy * halfHeight);
        } else {
            const right = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
            const up = this.temp.camUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
            const dist = camera.position.distanceTo(this.viewer.pivot.position);

            const halfH = dist * Math.tan(FOV_RAD / 2);
            const halfW = halfH * camera.aspect;
            camera.position.addScaledVector(right, dx * halfW);
            camera.position.addScaledVector(up, dy * halfH);
        }
    }

    private animateTowardOrho(zoomFactor: number, camera: THREE.OrthographicCamera) {
        const startTime = performance.now();
        const startLeft = camera.left;
        const endLeft = camera.left * zoomFactor;
        const startRight = camera.right;
        const endRight = camera.right * zoomFactor;
        const startTop = camera.top;
        const endTop = camera.top * zoomFactor;
        const startBottom = camera.bottom;
        const endBottom = camera.bottom * zoomFactor;

        const progressAnimation = () => {
            const now = performance.now();
            const progress = Math.min((now - startTime) / DOUBLE_CLICK_TIME, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const pivotBefore = this.viewer.pivot.position.clone().project(camera);

            camera.left = THREE.MathUtils.lerp(startLeft, endLeft, eased);
            camera.right = THREE.MathUtils.lerp(startRight, endRight, eased);
            camera.top = THREE.MathUtils.lerp(startTop, endTop, eased);
            camera.bottom = THREE.MathUtils.lerp(startBottom, endBottom, eased);

            camera.updateProjectionMatrix();

            const pivotAfter = this.viewer.pivot.position.clone().project(camera);
            const dx = pivotAfter.x - pivotBefore.x;
            const dy = pivotAfter.y - pivotBefore.y;
            const { halfWidth, halfHeight } = this.getHalfExtent(camera);

            const fwd = camera.getWorldDirection(this.temp.camFwd);
            const right = this.temp.camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
            const up = this.temp.camUp.crossVectors(right, fwd).normalize();

            camera.position.addScaledVector(right, dx * halfWidth);
            camera.position.addScaledVector(up, dy * halfHeight);

            this.viewer.updateNow();
            this.eval();

            if (progress < 1) {
                requestAnimationFrame(progressAnimation);
            }
        };

        requestAnimationFrame(progressAnimation);
    }

    private animateTowardPerspective(target: THREE.Vector3, zoomFactor: number, camera: THREE.PerspectiveCamera) {
        const startTime = performance.now();

        const minDistance = metersToScene(MIN_DISTANCE_M, this.app.state.sceneUnit);
        this.temp.targetToCam.subVectors(camera.position, target);
        const offset = this.temp.targetToCam.clone().multiplyScalar(zoomFactor).clampLength(minDistance, Infinity);
        const start = camera.position.clone();
        const end = target.clone().add(offset);

        const progressAnimation = () => {
            const now = performance.now();
            const progress = Math.min((now - startTime) / DOUBLE_CLICK_TIME, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            camera.position.lerpVectors(start, end, eased);

            this.viewer.updateNow();
            this.eval();

            if (progress < 1) {
                requestAnimationFrame(progressAnimation);
            }
        };

        requestAnimationFrame(progressAnimation);
    }

    private animateToward(target: THREE.Vector3, zoomFactor: number) {
        if (this.viewer.camera instanceof THREE.OrthographicCamera) {
            this.animateTowardOrho(zoomFactor, this.viewer.camera);
        } else {
            this.animateTowardPerspective(target, zoomFactor, this.viewer.camera);
        }
    }

    private getBoundaryBounds(boundary: GeoJSON.MultiPolygon): [number, number, number, number] {
        const bounds: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];

        for (const polygon of boundary.coordinates) {
            for (const ring of polygon) {
                for (const [x, y] of ring) {
                    bounds[0] = Math.min(bounds[0], x);
                    bounds[1] = Math.min(bounds[1], y);
                    bounds[2] = Math.max(bounds[2], x);
                    bounds[3] = Math.max(bounds[3], y);
                }
            }
        }

        return bounds;
    }

    private getBestProj(bounds: readonly [number, number, number, number, ...number[]]) {
        const { project_id } = this.project.get();

        const items = Object.values(this.app.state.datasets).flatMap((dataset) => {
            if (
                dataset.project_id !== project_id ||
                (dataset.kind !== "pointcloud" && dataset.kind !== "raster") ||
                !dataset.proj ||
                !dataset.bounds_wgs84
            ) {
                return [];
            }
            return [{ proj: dataset.proj, bounds: dataset.bounds_wgs84 }];
        });

        const min = new THREE.Vector2(bounds[0], bounds[1]);
        const max = new THREE.Vector2(bounds[2], bounds[3]);
        const target = new THREE.Box2(min, max);
        const targetSize = target.getSize(new THREE.Vector2());
        const targetArea = targetSize.x * targetSize.y;

        let bestProj = WM_EPSG;
        let bestIntersectionArea = 0;

        for (const proj of new Set(items.map((item) => item.proj))) {
            const projBounds = new THREE.Box2();
            for (const item of items) {
                if (item.proj !== proj) continue;

                projBounds.expandByPoint(new THREE.Vector2(item.bounds[0], item.bounds[1]));
                projBounds.expandByPoint(new THREE.Vector2(item.bounds[2], item.bounds[3]));
            }

            const size = projBounds.intersect(target).getSize(new THREE.Vector2());
            const area = size.x * size.y;
            if (area > bestIntersectionArea) {
                bestIntersectionArea = area;
                bestProj = proj;
            }
        }

        const targetCoverage = bestIntersectionArea / targetArea;
        if (targetArea <= 0 || targetCoverage < MIN_PROJ_COVERAGE) {
            return WM_EPSG;
        }

        return bestProj;
    }

    focusBounds(bounds: readonly [number, number, number, number, ...number[]]) {
        const { project_id } = this.project.get();
        const bestProj = this.getBestProj(bounds);
        if (bestProj !== this.app.state.proj) this.app.setSceneProj(bestProj);

        const projectBounds = this.project.bounds(project_id);
        const horizontalExtent = projectBounds ? this.app.boundsExtent(projectBounds) : this.project.extent(project_id);
        const center = horizontalExtent.getCenter(new THREE.Vector3());
        Object.assign(this.app.state.offset, { x: center.x, y: center.y, z: 0 });

        const minZ = bounds[4] ?? 0;
        const maxZ = bounds[5] ?? 0;
        const extent = this.app.boundsExtent(bounds, minZ, maxZ);
        const target = extent.getCenter(new THREE.Vector3());

        // Keep elevation filters based on the whole project rather than the focus area.
        this.app.adjustRanges(this.project.extent(project_id));

        this.lookAt(
            new THREE.Vector2(target.x, target.y),
            bounds.length >= 6 ? new THREE.Vector2(minZ, maxZ) : null,
            Math.max(extent.max.x - extent.min.x, extent.max.y - extent.min.y),
        );
    }

    focus(boundary?: GeoJSON.MultiPolygon) {
        if (boundary) {
            this.focusBounds(this.getBoundaryBounds(boundary));
            return;
        }

        const { project_id } = this.project.get();
        const bounds = this.project.bounds(project_id);
        if (bounds) {
            const extent = this.project.extent(project_id);
            this.focusBounds([...bounds, extent.min.z, extent.max.z]);
            return;
        }

        const extent = this.project.extent(project_id);
        const target = extent.getCenter(new THREE.Vector3());
        Object.assign(this.app.state.offset, { x: target.x, y: target.y, z: 0 });

        this.app.adjustRanges(extent);
        this.lookAt(
            new THREE.Vector2(target.x, target.y),
            new THREE.Vector2(extent.min.z, extent.max.z),
            Math.max(extent.max.x - extent.min.x, extent.max.y - extent.min.y),
        );
    }

    /** The basemap height model is metres, the scene is the scene CRS's own unit. */
    private elevationInScene(x: number, y: number) {
        const [wmX, wmY] = this.app.toWm(x, y);
        return metersToScene(getCachedElevation(wmX, wmY) ?? 0, this.app.state.sceneUnit);
    }

    private getTargetZ(targetXY: THREE.Vector2, zRange: THREE.Vector2 | null): number {
        // Assume 2D if zRange at 0 and use terrain elevation instead
        if (zRange && (zRange.x !== 0 || zRange.y !== 0)) {
            return (zRange.x + zRange.y) / 2;
        }

        return this.elevationInScene(targetXY.x, targetXY.y);
    }

    lookAt(targetXY: THREE.Vector2, zRange: THREE.Vector2 | null, size: number) {
        const targetZ = this.getTargetZ(targetXY, zRange);
        const target = new THREE.Vector3(targetXY.x, targetXY.y, targetZ);

        target.sub(this.app.state.offset);
        this.viewer.pivot.position.copy(target);

        const direction = new THREE.Vector3(0, -1, 1).normalize();

        if (this.viewer.camera instanceof THREE.OrthographicCamera) {
            const aspect = this.viewer.size.width / this.viewer.size.height;
            const halfWidth = size;
            const halfHeight = halfWidth / aspect;

            this.viewer.camera.left = -halfWidth;
            this.viewer.camera.right = halfWidth;
            this.viewer.camera.top = halfHeight;
            this.viewer.camera.bottom = -halfHeight;

            const position = this.viewer.getOrthoPosition(target, direction);
            if (position) this.viewer.camera.position.copy(position);
        } else {
            const dist = Math.min(size, maxCameraDist(this.app.state.sceneUnit));
            this.viewer.camera.position.copy(target).addScaledVector(direction, dist);
        }

        this.viewer.camera.lookAt(target);

        this.viewer.updateCameraNearFar();
        this.viewer.camera.updateMatrixWorld(true);

        this.lod.eval();
        this.terrain.init();
    }
}
